import { FunctionComponent } from "react";
import { Link } from "react-router-dom";

import {
	faBan,
	faBolt,
	faCirclePause,
	faCoin,
	faEnvelope,
	faLinkSlash,
	faMagnifyingGlass,
	faRotate,
	faTriangleExclamation,
	faXmark,
} from "@fortawesome/pro-regular-svg-icons";
import { IconDefinition } from "@fortawesome/fontawesome-svg-core";

import { classNames } from "@/utils/classNames";

import { Icon } from "@/components";

import { CampaignStatus } from "@/enum/campaign-status.enum";
import { UpdateType } from "@/enum/list.enum";

import { useUpdateListMutation } from "@/pages/Private/redux/list/list.api";

import { DashboardIssue, DashboardIssueType } from "../schema/dashboard";
import { issueToDisplay } from "../utils/issueToDisplay";

const TYPE_ICON: Record<DashboardIssueType, IconDefinition> = {
	[DashboardIssueType.LINKEDIN_DISCONNECTED]: faLinkSlash,
	[DashboardIssueType.LINKEDIN_SN_DISCONNECTED]: faLinkSlash,
	[DashboardIssueType.CRM_SYNC_FAILED]: faRotate,
	[DashboardIssueType.LIST_NO_RESULTS_ONGOING]: faMagnifyingGlass,
	[DashboardIssueType.LIST_NO_RESULTS_ONCE]: faMagnifyingGlass,
	[DashboardIssueType.LIST_OUTREACH_NOT_ACTIVATED]: faBolt,
	[DashboardIssueType.CREDITS_LOW]: faCoin,
	[DashboardIssueType.EMAIL_LOGGED_OUT]: faEnvelope,
	[DashboardIssueType.COMPETITORS_NEED_REVIEW]: faBan,
	[DashboardIssueType.HUBSPOT_NOTE_OWNER_MISSING]: faRotate,
	[DashboardIssueType.CAMPAIGN_PAUSED_UNSENDABLE]: faCirclePause,
	[DashboardIssueType.DYNAMICS_OWNER_PRIVILEGE_MISSING]: faRotate,
	[DashboardIssueType.CRM_RECONNECT_REQUIRED]: faLinkSlash,
	[DashboardIssueType.ZOHO_LINKEDIN_FIELD_NOT_SEARCHABLE]: faMagnifyingGlass,
};

const severityBg = (severity: DashboardIssue["severity"]) =>
	severity === "error"
		? "bg-status-error-light text-status-error"
		: "bg-status-warning-light text-text-warning";

const PRIMARY_ACTION_CLASS =
	"text-xs font-medium text-white bg-blue-action hover:shadow-md rounded px-2.5 py-1 text-center";

export const DashboardIssueRow: FunctionComponent<{
	issue: DashboardIssue;
	onDismiss: () => void;
	onActivated: () => void;
}> = ({ issue, onDismiss, onActivated }) => {
	const display = issueToDisplay(issue);
	const [activateOutreach, { isLoading: isActivating }] = useUpdateListMutation();

	const handleActivate = async () => {
		if (!issue.entityId) {
			return;
		}

		try {
			// arch-allow list-activation: the "outreach isn't active" issue's Activate button, under copy
			// reading "until you activate outreach for the list" — the list-wide switch, by name.
			await activateOutreach({
				id: issue.entityId,
				updateType: UpdateType.STATUS_WITH_REFETCH,
				campaignStatus: CampaignStatus.RUNNING,
			}).unwrap();
			onActivated();
		} catch {
			// keep the issue visible so the user can retry
		}
	};

	const renderPrimaryAction = () =>
		display.primaryAction === "activate-outreach" ? (
			<button
				className={classNames(PRIMARY_ACTION_CLASS, "disabled:opacity-60")}
				disabled={isActivating}
				type="button"
				onClick={handleActivate}
			>
				{display.primaryActionLabel}
			</button>
		) : (
			<Link className={PRIMARY_ACTION_CLASS} to={display.primaryActionTo}>
				{display.primaryActionLabel}
			</Link>
		);

	return (
		<div className="flex items-start gap-3 px-4 py-3">
			<div
				className={classNames(
					"w-8 h-8 rounded-md flex items-center justify-center shrink-0",
					severityBg(issue.severity)
				)}
			>
				<Icon className="w-4 h-4" icon={TYPE_ICON[issue.type] ?? faTriangleExclamation} />
			</div>
			<div className="flex-1 min-w-0">
				<div className="text-sm font-medium text-text">{display.title}</div>
				<div className="text-xs text-text-meta">{display.description}</div>
			</div>
			<div className="flex flex-col gap-1.5 shrink-0">{renderPrimaryAction()}</div>
			<button
				aria-label="Dismiss issue"
				className="w-6 h-6 rounded-md text-text-meta hover:bg-background-light flex items-center justify-center shrink-0"
				type="button"
				onClick={onDismiss}
			>
				<Icon className="w-3 h-3" icon={faXmark} />
			</button>
		</div>
	);
};
